import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import ErrorIcon from '@material-ui/icons/Error';
import { searchPeople2 } from 'reducers/SearchReducer';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { useDispatch } from 'react-redux';
import { RMSAutocomplete } from '../../../shared/components/form-controls/index';
import { executeApiAction } from '../../utility/apiHelper';
import { RMSButton, RMSIconButton } from '../../../shared/components/buttons/index';
import { connect, useSelector } from 'react-redux';
import { showEditPerson } from 'reducers/PersonDialogsReducer';
import { getPersonWarrant } from 'reducers/WarrantReducer';
import { CodeTagTypeIconRender } from 'utils/functions';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { showWarrants } from 'reducers/PersonDialogsReducer';
import { formatDisplayDate } from '../../../shared/utility/dateFormatter';
import { UrgentMessageIcon } from '../../../shared/utility/UrgentMessageHelper';

const SearchPersonAutoComplete = ({
  name,
  label,
  onSelectionChange,
  meButtonVisible = false,
  placeholder,
  autoFocus = false,
  defaultPersonId = null,
  defaultPersonSelectedCallback = null,
  disabled = false,
  showAddIcon = true,
  onAddIconClick = () => {},
  showDetailsIcon = true,
  searchPersonCallback = null,
  disableClearable = false,
  optionValue = 'ptsPersonID',
  formatLabel = (person) => `${person.FirstName || ''} ${person.LastName || ''}`.trim(),
  showEditPerson,
  ...props
}) => {
  const dispatch = useDispatch();
  const isMounted = useRef(true);
  const currentUser = useSelector((state) => state.user.userData.user);

  const personPerms = getFullPermissions('globals', 'People', 'any');
  const canCreatePerson = personPerms.Create;

  const canViewPersonDetails = Boolean(personPerms.Read || personPerms.Edit);

  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchPersons = useCallback(
    async (searchText) => {
      const searchFunction = searchPersonCallback || searchPeople2;
      await executeApiAction({
        setLoading,
        action: async () => {
          return searchFunction(searchText, false, 0, 100);
        },
        onSuccess: (response) => {
          response = response || [];
          response.forEach((person) => {
            person.label = formatLabel(person);
            person.BirthDate = person.BirthDate && formatDisplayDate(person.BirthDate);
            person.OLNExpiration = person.OLNExpiration && formatDisplayDate(person.OLNExpiration);
          });
          setPersons(response);
        },
        onError: (error) => {
          dispatch(notify(error.message, 'error'));
        },
      });
    },
    [dispatch, formatLabel, searchPersonCallback]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((searchText) => {
        if (!searchText || searchText.trim().length < 3) {
          setPersons([]);
          return;
        }

        searchPersons(searchText);
      }, 500),
    [searchPersons]
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleDefaultPersonId = useCallback(
    async (personId, callback = defaultPersonSelectedCallback) => {
      if (!personId) return;
      await executeApiAction({
        setLoading,
        dispatch,
        action: () => searchPeople2('', false, 0, 100, personId),
        onSuccess: (response) => {
          const fetchPerson = response?.[0];
          if (!fetchPerson) {
            dispatch(notify('Person not found.', 'warning'));
            return;
          }
          fetchPerson.BirthDate = fetchPerson.BirthDate && formatDisplayDate(fetchPerson.BirthDate);
          fetchPerson.OLNExpiration =
            fetchPerson.OLNExpiration && formatDisplayDate(fetchPerson.OLNExpiration);
          setPersons((prev) => {
            const exists = prev.some((person) => person.ptsPersonID === fetchPerson.ptsPersonID);
            return exists ? prev : [fetchPerson, ...prev];
          });

          callback?.(fetchPerson);
        },
      });
    },
    [defaultPersonSelectedCallback, dispatch]
  );

  const handleMeClick = useCallback(() => {
    if (!currentUser?.ptsPersonID) {
      dispatch(notify('Logged in user not found.', 'warning'));
      return;
    }

    handleDefaultPersonId(currentUser.ptsPersonID, onSelectionChange);
  }, [currentUser, dispatch, handleDefaultPersonId, onSelectionChange]);

  const getWarrants = useCallback(
    async (value) => {
      await executeApiAction({
        setLoading,
        dispatch,
        action: () => getPersonWarrant(value?.ptsPersonID),
        onSuccess: (response) => {
          const Name = `${value?.FirstName} ${value?.LastName}`;
          props.showWarrants({ Name, warrants: response });
        },
      });
    },
    [dispatch, props]
  );

  const renderOption = (option) => {
    let namePart = '';
    if (option.LastName) namePart += option.LastName;
    if (option.Suffix) namePart += ` ${option.Suffix},`;
    if (!option.Suffix) namePart += ',';
    if (option.FirstName) namePart += ` ${option.FirstName} `;
    if (option.MiddleName) namePart += ` ${option.MiddleName} `;
    namePart = namePart.trim();

    // Collect remaining detail parts, only including ones that exist
    const detailParts = [];

    if (option.Race) {
      const desc = props.dictionary.codeRace.find((s) => s.Code === option.Race);
      if (desc) detailParts.push(`Race: ${desc.Description}`);
    }
    if (option.Sex) {
      const desc = props.dictionary.codeSex.find((s) => s.Code === option.Sex);
      if (desc) detailParts.push(`Sex: ${desc.Description}`);
    }
    if (option.BirthDate) {
      detailParts.push(`DOB: ${option.BirthDate}`);
    }
    if (option.OLN) detailParts.push(`OLN: ${option.OLN}`);
    if (option.SSN) detailParts.push(`SSN: ${option.SSN}`);
    if (option.PersonID) detailParts.push(`${option.PersonID}`);

    // Join name + details with a single separator, only where both sides exist
    const label = [namePart, detailParts.join(' | ')].filter(Boolean).join(' | ');

    let tagIcon = '';
    if (option.Tags) {
      const json_data = JSON.parse(option.Tags);
      tagIcon = (
        <span>
          {json_data.map((tag, idx) => {
            const icon = tag.c[0].Icon;
            const tagData = {
              created: tag.Created,
              createdBy: tag.CreatedBy,
              updated: tag.Updated,
              updatedBy: tag.UpdatedBy,
              type: tag.Type,
              typeDescription: tag.c[0].TypeDescription,
              description: tag.Value,
            };
            const TagIcon = CodeTagTypeIconRender(icon, 'small', '20px', tagData);
            return <span key={idx}>{TagIcon}</span>;
          })}
        </span>
      );
    }

    return (
      <span className={option.ActiveWarrant ? 'active-warrant-option' : ''}>
        {label}
        {tagIcon}
        {option?.IsUser && (
          <RMSIconButton
            icon={<HowToRegIcon />}
            tooltip="Existing user"
            size="small"
            className="active-warrant-icon"
          />
        )}
        <RMSIconButton
          icon={UrgentMessageIcon(option)}
          size="small"
          className="active-warrant-icon"
        />
      </span>
    );
  };

  useEffect(() => {
    handleDefaultPersonId(defaultPersonId);
  }, [defaultPersonId, handleDefaultPersonId]);

  return (
    <Box className="rms-person-autocomplete-wrapper">
      {meButtonVisible && (
        <RMSButton
          label="Me"
          size="small"
          className="rms-me-chip"
          disabled={disabled}
          onClick={handleMeClick}
        />
      )}

      <RMSAutocomplete
        name={name}
        label={label}
        placeholder={placeholder}
        getOptionLabel={(option) => formatLabel(option)}
        renderOption={renderOption}
        optionLabel="FirstName"
        optionValue={optionValue}
        options={persons}
        leftContent={() =>
          showAddIcon &&
          canCreatePerson && (
            <RMSIconButton
              icon={<AddIcon fontSize="small" />}
              tooltip="Add Person"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onAddIconClick?.(event);
              }}
            />
          )
        }
        rightContent={({ value }) =>
          value &&
          value.ptsPersonID > 0 && (
            <div className="person-action-icons">
              {showDetailsIcon && canViewPersonDetails && (
                <RMSIconButton
                  icon={<InfoIcon />}
                  tooltip="Person Info"
                  size="small"
                  color="primary"
                  className={value.hasAnyUrgentMessage ? 'urgent-message-icon' : ''}
                  onClick={(event) => {
                    event.stopPropagation();
                    showEditPerson({ ptsPersonID: value.ptsPersonID });
                  }}
                />
              )}

              {value.ActiveWarrant && (
                <RMSIconButton
                  icon={<ErrorIcon />}
                  tooltip="Active Warrants"
                  size="small"
                  className="active-warrant-icon"
                  onClick={(event) => {
                    event.stopPropagation();
                    getWarrants(value);
                  }}
                />
              )}
            </div>
          )
        }
        onSelectionChange={(data) => onSelectionChange && onSelectionChange(data)}
        onSearch={debouncedSearch}
        disabled={disabled}
        loading={loading}
        autoFocus={autoFocus}
        disableClearable={disableClearable}
      />
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    dictionary: state.dictionary,
    dialogs: state.dialogs,
  };
};

export default connect(mapStateToProps, { showEditPerson, showWarrants })(SearchPersonAutoComplete);
